import { NavLink } from 'react-router'
import { Icon, type IconName } from '#app/components/ui/icon.tsx'
import { cn } from '#app/utils/misc.tsx'

/**
 * Navigation chrome (plan §1.7 OyShell parts). The tab bar is a flat bar
 * with a top hairline: a line icon with its word under it (fase 5b: no
 * emoji as icons), a 52×30 accent-soft pill behind the selected (filled)
 * icon, a badge when unread or a warm dot when something asks for this
 * person. `big` is Else's scale: 68px tabs, nothing under 18px.
 * NavLink sets `aria-current="page"` on the active item; the CSS keys the
 * pill and colour off that attribute, so screen readers and eyes agree.
 */
export type OyTabItem = {
	to: string
	label: string
	icon: IconName
	/** the filled sprite variant, shown when selected */
	iconFill?: IconName
	badge?: string | number
	/** the warm dot: something asks for this person */
	dot?: boolean
	end?: boolean
}

export function OyTabBar({
	items,
	label = 'Hoofdnavigatie',
	big,
	inline,
	className,
}: {
	items: OyTabItem[]
	label?: string
	/** Else's scale */
	big?: boolean
	/** render in flow (the design-system preview) instead of fixed to the bottom */
	inline?: boolean
	className?: string
}) {
	return (
		<nav
			aria-label={label}
			className={cn('oy-tabbar', big && 'big', inline && 'inline', className)}
		>
			{items.map((item) => (
				<NavLink key={item.to} to={item.to} end={item.end} className="oy-tab">
					{({ isActive }) => (
						<>
							<span className="oy-tab-pill">
								<Icon
									name={isActive && item.iconFill ? item.iconFill : item.icon}
									className="oy-tab-ico"
									aria-hidden="true"
								/>
								<TabMark item={item} />
							</span>
							<span>{item.label}</span>
						</>
					)}
				</NavLink>
			))}
		</nav>
	)
}

/** The badge (a count) or the warm dot on a tab. */
export function TabMark({ item }: { item: Pick<OyTabItem, 'badge' | 'dot'> }) {
	if (item.badge != null && item.badge !== 0)
		return (
			<span className="oy-tab-badge">
				{item.badge}
				<span className="sr-only"> nieuw</span>
			</span>
		)
	if (item.dot)
		return (
			<span className="oy-tab-dot">
				<span className="sr-only"> vraagt iets van je</span>
			</span>
		)
	return null
}

/** Desktop: the same four destinations as a pill nav in the header. */
export function OyTopNav({
	items,
	label = 'Hoofdnavigatie',
	className,
}: {
	items: OyTabItem[]
	label?: string
	className?: string
}) {
	return (
		<nav aria-label={label} className={cn('oy-topnav', className)}>
			{items.map((item) => (
				<NavLink
					key={item.to}
					to={item.to}
					end={item.end}
					className="oy-topnav-item"
				>
					{({ isActive }) => (
						<>
							<Icon
								name={isActive && item.iconFill ? item.iconFill : item.icon}
								className="oy-topnav-ico"
								aria-hidden="true"
							/>
							{item.label}
							{item.badge != null && item.badge !== 0 ? (
								<span className="oy-pill oy-pill-done">
									{item.badge}
									<span className="sr-only"> nieuw</span>
								</span>
							) : null}
						</>
					)}
				</NavLink>
			))}
		</nav>
	)
}
